import { Editor } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import { css, html, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { SearchAndReplace } from './search-and-replace'
import { PasteUrlExtension } from './paste-url'

@customElement('jump-rich-text-editor-with-search')
export default class RichTextEditorWithSearch extends LitElement {
  @property()
  content: string

  @property()
  label: string

  @property()
  name: string

  @property()
  copyToInputs: string

  @property()
  form: HTMLFormElement | null

  @state()
  private _editor

  @state()
  private _textDropdown = false

  @state()
  private _inputs: HTMLInputElement[] = []

  static styles = css`
    #element {
      width: 100%;
      height: 100%;
      font-family:
        'TT Hoves Pro',
        ui-sans-serif,
        system-ui,
        sans-serif,
        -apple-system,
        'system-ui',
        'Segoe UI',
        Roboto,
        'Helvetica Neue',
        Arial,
        'Noto Sans',
        'Apple Color Emoji',
        'Segoe UI Emoji',
        'Segoe UI Symbol',
        'Noto Color Emoji';
    }

    strong,
    b {
      font-weight: 600;
    }

    u {
      text-decoration: underline;
    }

    ul p,
    ol p {
      margin: 0px;
    }

    #toolbar {
      border-bottom: 1px solid #dfe4e7;
      padding-bottom: 0.05rem;
      position: sticky;
      top: 0;
      background-color: white;
      z-index: 100;

      button {
        border: none;
        font-size: 1rem;
        font-weight: 500;
        padding: 0.3rem 0.6rem 0.2rem;
        cursor: pointer;
        border-radius: 0.5rem;

        &:hover,
        &.active {
          background-color: #f0f4f5;
        }
      }

      .text-button {
        display: flex;
        align-items: center;
        font-size: 15px;
      }

      .text-button:hover {
        background-color: #f0f4f5;
      }

      .text-button:focus {
        background-color: white;
      }
    }

    #toolbar input {
      padding: 0 0.5rem;
      border-radius: 0.5rem;
      margin-top: 0.1rem;
      max-height: 1.9rem;
      border: 1px solid #dfe4e7;
      background-color: white;
      color: black;
      outline: none;
      font-size: 15px;
    }

    #toolbar input::placeholder {
      color: #a7b0ba;
    }

    #toolbar input#replace_input {
      border: none;
      line-height: 10px;
      height: fit-content;
      border-radius: 0;
    }

    #toolbar input:focus {
      border-color: #999;
    }

    #toolbar button {
      margin-top: 0.1rem;
      max-height: 1.9rem;
      outline: none;
    }

    #toolbar button:focus {
      background-color: #f0f0f0;
    }

    #toolbar button {
      background-color: white;
    }

    #toolbar button.disabled {
      color: #999;
      cursor: default;
    }

    #toolbar button.disabled:hover {
      background-color: white;
    }

    .toolbar-section,
    .text-dropdown {
      background-color: white;
      z-index: 1000;
      display: flex;
      gap: 0.2rem;
      color: black;
      position: relative;
      padding: 0.1rem 0.2rem;

      button {
        border: none;
        font-size: 1rem;
        font-weight: 500;
        padding: 0.3rem 0.6rem 0.2rem;
        cursor: pointer;
        border-radius: 0.5rem;

        &:hover,
        &.active {
          background-color: #dfe4e7;
        }
      }

      .text-button {
        display: flex;
        align-items: center;
        font-size: 15px;
      }
    }

    .tiptap {
      height: 100%;
      outline: none;
    }

    .tiptap {
      table {
        border-collapse: collapse;
        table-layout: fixed;
        width: 100%;
        margin: 0;
        overflow: hidden;

        td,
        th {
          min-width: 1em;
          border: 2px solid #ced4da;
          padding: 3px 5px;
          vertical-align: top;
          box-sizing: border-box;
          position: relative;

          > * {
            margin-bottom: 0;
          }
        }

        th {
          font-weight: 600;
          text-align: left;
          background-color: #f1f3f5;
        }

        .selectedCell:after {
          z-index: 2;
          position: absolute;
          content: '';
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          background: rgba(200, 200, 255, 0.4);
          pointer-events: none;
        }

        .column-resize-handle {
          position: absolute;
          right: -2px;
          top: 0;
          bottom: -2px;
          width: 4px;
          background-color: #adf;
          pointer-events: none;
        }

        p {
          margin: 0;
        }
      }
    }

    .tableWrapper {
      padding: 1rem 0;
      overflow-x: auto;
    }

    .resize-cursor {
      cursor: ew-resize;
      cursor: col-resize;
    }

    .text-dropdown {
      position: absolute;
      top: 3rem;
      left: 0;
      width: 60px;
      display: flex;
      flex-direction: column;

      button {
        background-color: white;
        text-align: left;
      }
    }

    .search-result {
      background-color: #ffdf90;
    }

    .search-result-current {
      background-color: #ffdf90;
    }

    #replace_input {
      width: 6rem;
    }

    #replace-input-wrapper {
      display: inline-block;
      padding: 0.3rem 0;
      border-radius: 0.5rem;
      border: 1px solid rgb(223, 228, 231);
    }

    #replace-input-wrapper:focus-within {
      border-color: #999;
      background-color: white;
    }

    #replace-input-wrapper:hover {
      background-color: #f0f4f5;

      #replace_input {
        background-color: #f0f4f5;
      }
    }

    #replace-input-wrapper:hover:focus-within {
      background-color: white;

      #replace_input {
        background-color: white;
      }
    }

    #find_input {
      width: 8rem;
    }

    .replace-icon {
      display: inline-block;
      padding-top: 0.6rem;
      margin: 0 0.4rem;
    }

    .hidden {
      display: none;
    }
  `

  firstUpdated() {
    let component = this

    JSON.parse(this.copyToInputs || '[]').forEach((id) => {
      let input = document.getElementById(id) as HTMLInputElement
      if (input) {
        this._inputs.push(input)
      }
    })
    let value = this.content
    let name = this.name

    this._inputs.forEach((input) => {
      input.value = value

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const newValue = input.value
            if (!this._editor.isFocused) {
              this._editor.commands.setContent(newValue, false)
            }
          }
        }
      })
      observer.observe(input, { attributes: true })
    })

    const CustomImage = Image.extend({
      addAttributes() {
        return {
          src: {
            default: null,
          },
          width: {
            default: null,
          },
        }
      },
    })

    this._editor = new Editor({
      autofocus: true,
      element: this.renderRoot.querySelector('#element'),
      extensions: [
        StarterKit,
        Link,
        PasteUrlExtension,
        Underline,
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        // Default TableCell
        // TableCell,
        // Custom TableCell with backgroundColor attribute
        // CustomTableCell,
        CustomImage.configure({
          inline: true,
        }),
        TextAlign.configure({
          types: ['paragraph'],
        }),
        SearchAndReplace.configure({
          searchResultClass: 'search-result', // class to give to found items. default 'search-result'
          caseSensitive: false, // no need to explain
          disableRegex: true, // also no need to explain
        }),
      ],
      content: value,
      onUpdate({ editor }) {
        component.requestUpdate()
        component._textDropdown = false
        const content = editor.getHTML()
        component.dispatchEvent(
          new CustomEvent('content-changed', {
            detail: { content: content, target: name },
            bubbles: true,
          })
        )
        component._inputs.forEach((input) => {
          input.value = content
          input.dispatchEvent(new Event('input', { bubbles: true }))
        })
      },
      onSelectionUpdate({ editor }) {
        component.requestUpdate()
        component._textDropdown = false
      },
    })

    let findInput = this.renderRoot.getElementById('find_input')
    let replaceInput = this.renderRoot.getElementById('replace_input')

    // updating the Apply button state
    replaceInput.addEventListener('input', () => {
      this._updateApplyState()
    })
    findInput.addEventListener('input', () => {
      this._updateApplyState()
    })

    // if Enter is pressed, replace the text
    replaceInput.addEventListener('keyup', (e: any) => {
      if (e.key === 'Enter') {
        this._replace_all()
      }
    })
  }

  render() {
    return html`
      <div style="height: 100%; display: block;">
        <div id="toolbar" style="text-align: right; display: flex; justify-content: space-between;">
          <div class="toolbar-section">
            <button class="text-button" @click=${this._toggleTextDropdown}>
              ${this._textStyle()}
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M5.22953 7.21048C5.37279 7.07265 5.56491 6.99734 5.76367 7.00109C5.96243 7.00484 6.15157 7.08735 6.28953 7.23048L9.99953 11.1685L13.7095 7.23048C13.777 7.15614 13.8586 7.09608 13.9497 7.05388C14.0408 7.01168 14.1394 6.98819 14.2397 6.9848C14.34 6.98141 14.44 6.99819 14.5337 7.03415C14.6274 7.07011 14.713 7.12451 14.7853 7.19413C14.8576 7.26376 14.9152 7.34719 14.9546 7.43947C14.9941 7.53176 15.0146 7.63103 15.015 7.73141C15.0154 7.83178 14.9957 7.93121 14.9569 8.02381C14.9182 8.11641 14.8613 8.20029 14.7895 8.27048L10.5395 12.7705C10.4696 12.8431 10.3857 12.9008 10.2929 12.9402C10.2001 12.9797 10.1003 13 9.99953 13C9.89871 13 9.79893 12.9797 9.70615 12.9402C9.61337 12.9008 9.52949 12.8431 9.45953 12.7705L5.20953 8.27048C5.0717 8.12722 4.99638 7.9351 5.00013 7.73634C5.00388 7.53758 5.08639 7.34844 5.22953 7.21048Z"
                  fill="black"
                />
              </svg>
            </button>
            ${this._textDropdown
              ? html`<div class="text-dropdown">
                  <button @click=${this._text}>Text</button>
                  <button @click=${this._h1} class="text-h1">H<sub>1</sub></button>
                  <button @click=${this._h2} class="text-h2">H<sub>2</sub></button>
                  <button @click=${this._h3} class="text-h3">H<sub>3</sub></button>
                  <button @click=${this._h4} class="text-h4">H<sub>4</sub></button>
                  <button @click=${this._h5} class="text-h5">H<sub>5</sub></button>
                </div> `
              : ''}

            <button
              @click="${this._bold}"
              class=${classMap({ active: this._editor ? this._editor.isActive('bold') : false })}
            >
              <svg width="18" height="13" viewBox="0 0 10 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M2 5.25H5.375C6.41052 5.25 7.25 4.41053 7.25 3.375C7.25 2.33947 6.41052 1.5 5.375 1.5H2V5.25ZM9.5 8.625C9.5 10.489 7.98898 12 6.125 12H0.5V0H5.375C7.23898 0 8.75 1.51104 8.75 3.375C8.75 4.27823 8.39517 5.09857 7.8173 5.70435C8.82343 6.28852 9.5 7.37775 9.5 8.625ZM2 6.75V10.5H6.125C7.16052 10.5 8 9.66053 8 8.625C8 7.58948 7.16052 6.75 6.125 6.75H2Z"
                  fill="black"
                />
              </svg>
            </button>
            <button
              @click="${this._italic}"
              class=${classMap({ active: this._editor ? this._editor.isActive('italic') : false })}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.25 15H5.25V13.5H7.44496L9.03187 4.5H6.75V3H12.75V4.5H10.555L8.96813 13.5H11.25V15Z"
                  fill="black"
                />
              </svg>
            </button>
            <button
              @click="${this._underline}"
              class=${classMap({ active: this._editor ? this._editor.isActive('underline') : false })}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M5.25 2.25V9.75C5.25 11.4069 6.59315 12.75 8.25 12.75H9.75C11.4069 12.75 12.75 11.4069 12.75 9.75V2.25H11.25V9.75C11.25 10.5784 10.5784 11.25 9.75 11.25H8.25C7.42157 11.25 6.75 10.5784 6.75 9.75V2.25H5.25ZM4.5 15H13.5V16.5H4.5V15Z"
                  fill="black"
                />
              </svg>
            </button>
            <button
              @click="${this._bulletList}"
              class=${classMap({ active: this._editor ? this._editor.isActive('bulletList') : false })}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 3H15.75V4.5H6V3ZM3.375 4.875C2.75368 4.875 2.25 4.37132 2.25 3.75C2.25 3.12868 2.75368 2.625 3.375 2.625C3.99632 2.625 4.5 3.12868 4.5 3.75C4.5 4.37132 3.99632 4.875 3.375 4.875ZM3.375 10.125C2.75368 10.125 2.25 9.6213 2.25 9C2.25 8.3787 2.75368 7.875 3.375 7.875C3.99632 7.875 4.5 8.3787 4.5 9C4.5 9.6213 3.99632 10.125 3.375 10.125ZM3.375 15.3C2.75368 15.3 2.25 14.7963 2.25 14.175C2.25 13.5537 2.75368 13.05 3.375 13.05C3.99632 13.05 4.5 13.5537 4.5 14.175C4.5 14.7963 3.99632 15.3 3.375 15.3ZM6 8.25H15.75V9.75H6V8.25ZM6 13.5H15.75V15H6V13.5Z"
                  fill="black"
                />
              </svg>
            </button>
            <button
              @click="${this._orderedList}"
              class=${classMap({ active: this._editor ? this._editor.isActive('orderedList') : false })}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.31268 2.625H3.538L2.4375 2.91988V4.08436L3.18752 3.88337L3.18763 6.375H2.25V7.5H5.25V6.375H4.31268V2.625ZM7.5 3H15.75V4.5H7.5V3ZM7.5 8.25H15.75V9.75H7.5V8.25ZM7.5 13.5H15.75V15H7.5V13.5ZM2.15625 11.7188C2.15625 10.8385 2.86979 10.125 3.75 10.125C4.63021 10.125 5.34375 10.8385 5.34375 11.7188C5.34375 12.0829 5.22137 12.419 5.0156 12.6875L5.01203 12.6922L3.9869 13.875H5.25V15H2.25009L2.24969 14.1589L4.10378 12.0262C4.17548 11.9439 4.21875 11.8366 4.21875 11.7188C4.21875 11.4599 4.00889 11.25 3.75 11.25C3.50533 11.25 3.3043 11.4376 3.2831 11.6766L3.27382 11.9062H2.15625V11.7188Z"
                  fill="black"
                />
              </svg>
            </button>
          </div>

          <div class="${this.hasAttribute('data-allow-find-replace') ? 'toolbar-section' : 'hidden'}">
            <input @input=${this._search_term_change} id="find_input" placeholder="Find..." />
            <span class="replace-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M16 13L19 16M19 16L16 19M19 16H5M8 11L5 8M5 8L8 5M5 8H19"
                  stroke="#999"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span id="replace-input-wrapper" style="padding-right: 6px;">
              <input id="replace_input" placeholder="Replace with..." />
              <span id="find-counter" style="display: inline-block; color: #999; min-width: 2rem;"></span>
            </span>
            <button
              @click=${this._replace_all}
              id="search-apply"
              class="text-button disabled"
              style="margin-left: 6px; margin-right: 6px;"
            >
              Apply
            </button>
          </div>
        </div>

        <div style="padding: 1rem 1rem 3rem;">
          <div id="element"></div>
        </div>
      </div>
    `
  }

  private _textStyle() {
    if (!this._editor) return
    if (this._editor.isActive('heading', { level: 1 })) {
      return html`<span>H<sub>1</sub></span>`
    }
    if (this._editor.isActive('heading', { level: 2 })) {
      return html`<span>H<sub>2</sub></span>`
    }
    if (this._editor.isActive('heading', { level: 3 })) {
      return html`<span>H<sub>3</sub></span>`
    }
    if (this._editor.isActive('heading', { level: 4 })) {
      return html`<span>H<sub>4</sub></span>`
    }
    if (this._editor.isActive('heading', { level: 5 })) {
      return html`<span>H<sub>5</sub></span>`
    }
    return html`<span>Text</span>`
  }

  private _next(_e: Event) {
    this._editor.commands.nextSearchResult()
    this._scrollToSelection()
  }

  private _previous(_e: Event) {
    this._editor.commands.previousSearchResult()
    this._scrollToSelection()
  }

  private _replace(_e: Event) {
    let input = this.renderRoot.getElementById('replace_input')
    this._editor.commands.setReplaceTerm(input.value)
    this._editor.commands.replace()
    this._scrollToSelection()
  }

  private _updateApplyState() {
    const findInput = this.renderRoot.getElementById('find_input')
    const replaceInput = this.renderRoot.getElementById('replace_input')
    const button = this.renderRoot.querySelector('#search-apply')

    const { results } = this._editor.storage.searchAndReplace

    if (findInput.value && results.length > 0) {
      button.classList.remove('disabled')
    } else {
      button.classList.add('disabled')
    }
  }

  private _scrollToSelection() {
    if (!this._editor) return

    const { results, resultIndex } = this._editor.storage.searchAndReplace
    const position: Range = results[resultIndex]

    if (!position) return

    this._editor.commands.setTextSelection(position)

    const { node } = this._editor.view.domAtPos(this._editor.state.selection.anchor)
    node instanceof HTMLElement && node.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  private _replace_all(_e: Event) {
    let findInput = this.renderRoot.getElementById('find_input')
    let replaceInput = this.renderRoot.getElementById('replace_input')
    let counter = this.renderRoot.getElementById('find-counter')

    this._editor.commands.setReplaceTerm(replaceInput.value)
    this._editor.commands.replaceAll()
    this._search_term_change({ target: { value: this._editor.storage.searchAndReplace.searchTerm } })

    findInput.value = ''
    findInput.focus()
    replaceInput.value = ''

    counter.textContent = ''

    this._editor.commands.setReplaceTerm('')
    this._editor.commands.setSearchTerm('')

    this._updateApplyState()
  }

  private _search_term_change(e: Event) {
    let counter = this.renderRoot.getElementById('find-counter')

    this._editor.commands.setSearchTerm(e.target.value)
    const { results } = this._editor.storage.searchAndReplace

    if (e.target.value === '') {
      counter.textContent = ''
      return
    }

    counter.textContent = '×' + results.length.toString()
  }

  private _search_and_replace_close(e: Event) {
    let menu = this.renderRoot.getElementById('search_and_replace_menu')
    menu.style.setProperty('display', 'none')

    this._editor.commands.setSearchTerm('')
  }

  private _search(_e: Event) {
    if (!this._editor) return

    const menu = this.renderRoot.getElementById('search_and_replace_menu')

    const { from, to } = this._editor.state.selection
    var selectedText = this._editor.state.doc.textBetween(from, to)

    this._editor.commands.setSearchTerm(selectedText)

    let searchTerm = this.renderRoot.getElementById('find_input')
    searchTerm.value = selectedText

    // why wouldn't it work without a timeout?
    setTimeout(() => {
      searchTerm.focus()
    }, 100)

    menu.style.setProperty('display', 'block')

    // effectively closes the bubble menu
    this._editor.commands.setTextSelection(0)
  }

  // Events below here used to include .focus() in the chain, but this was causeing
  // the editor to lose focus when the button was clicked. We can't tell what it was used for
  // so we're just going to remove it for now.

  private _bold(e: Event) {
    this._editor && this._editor.chain().toggleBold().run()
  }

  private _italic(e: Event) {
    this._editor && this._editor.chain().toggleItalic().run()
  }

  private _underline(e: Event) {
    this._editor && this._editor.chain().toggleUnderline().run()
  }

  private _bulletList(e: Event) {
    this._editor && this._editor.chain().toggleBulletList().run()
  }

  private _orderedList(e: Event) {
    this._editor && this._editor.chain().toggleOrderedList().run()
  }

  private _toggleTextDropdown(e: Event) {
    this._textDropdown = !this._textDropdown
  }

  private _text(e: Event) {
    this._editor && this._editor.chain().clearNodes().run()
  }

  private _h1(e: Event) {
    this._editor && this._editor.chain().setHeading({ level: 1 }).run()
  }

  private _h2(e: Event) {
    this._editor && this._editor.chain().setHeading({ level: 2 }).run()
  }

  private _h3(e: Event) {
    this._editor && this._editor.chain().setHeading({ level: 3 }).run()
  }

  private _h4(e: Event) {
    this._editor && this._editor.chain().setHeading({ level: 4 }).run()
  }

  private _h5(e: Event) {
    this._editor && this._editor.chain().setHeading({ level: 5 }).run()
  }

  private _copy(e: Event) {
    if (!this._editor) return
    const editorContent = this._editor.getHTML()
    const tempElem = document.createElement('div')
    tempElem.innerHTML = editorContent

    navigator.clipboard
      .write([
        new ClipboardItem({
          'text/html': new Blob([tempElem.innerHTML], { type: 'text/html' }),
        }),
      ])
      .then(() => {
        console.log('Content copied to clipboard')
      })
      .catch((err) => {
        console.error('Failed to copy: ', err)
      })
  }
}
